// src/components/roleplay/ObjectionReview.js
// Admin approval queue for objections reps flag during roleplay calls.
// Approving writes the objection into that persona's knownObjections library.
// Self-contained — no outer page chrome.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { addKnownObjection, subscribeObjections, setObjectionStatus } from '../../data/roleplay';

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const SectionSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0 0 16px 0;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-right: 2px;
`;

const Chip = styled.button`
  appearance: none;
  border: 1px solid ${(props) => (props.$active ? '#0e8c6a' : '#e2e8f0')};
  background: ${(props) => (props.$active ? '#e6f5f0' : '#ffffff')};
  color: ${(props) => (props.$active ? '#0e8c6a' : '#475569')};
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0e8c6a;
  }
`;

const ChipCount = styled.span`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#94a3b8')};
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const QuoteText = styled.blockquote`
  margin: 0;
  padding: 0;
  font-size: 15px;
  color: #1e293b;
  font-style: italic;
  line-height: 1.5;

  &::before {
    content: '\\201C';
  }
  &::after {
    content: '\\201D';
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #0e8c6a;
  background: #e6f5f0;
  white-space: nowrap;
`;

const StatusBadge = styled(Badge)`
  color: ${(props) =>
    props.$status === 'approved' ? '#0e8c6a' : props.$status === 'rejected' ? '#dc2626' : '#d97706'};
  background: ${(props) =>
    props.$status === 'approved' ? '#e6f5f0' : props.$status === 'rejected' ? '#fef2f2' : '#fef3c7'};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 12px;
`;

const MetaItem = styled.span`
  strong {
    color: #475569;
    font-weight: 600;
  }
`;

const Muted = styled.span`
  color: #94a3b8;
`;

const NoteRow = styled.div`
  margin-bottom: 12px;
`;

const inputStyles = `
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #1e293b;
  font-family: inherit;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #0e8c6a;
    box-shadow: 0 0 0 3px rgba(14, 140, 106, 0.12);
  }
`;

const NoteInput = styled.input`
  ${inputStyles}
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid #0e8c6a;
  background: #0e8c6a;
  color: #fff;
  cursor: pointer;

  &:hover {
    background: #0b7057;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RejectButton = styled(Button)`
  background: #ffffff;
  color: #dc2626;
  border-color: #fecaca;

  &:hover {
    background: #fef2f2;
  }
`;

const HelpNote = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const ErrorState = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  margin: 8px 0 24px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  margin-bottom: 10px;
`;

function formatDate(ts) {
  const d = ts?.toDate?.();
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const FILTERS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

function ObjectionCard({ objection, onApprove, onReject, busy }) {
  const [note, setNote] = useState(objection.note || '');
  const [rowError, setRowError] = useState('');
  const [rowNotice, setRowNotice] = useState('');

  async function handleApprove() {
    setRowError('');
    setRowNotice('');
    try {
      if (objection.personaId) {
        await onApprove(objection, note);
      } else {
        await onApprove(objection, note, { skippedLibrary: true });
        setRowNotice('Approved — no persona linked, so it was not added to a persona library.');
      }
    } catch (err) {
      setRowError(err?.message || 'Failed to approve.');
    }
  }

  async function handleReject() {
    setRowError('');
    setRowNotice('');
    try {
      await onReject(objection, note);
    } catch (err) {
      setRowError(err?.message || 'Failed to reject.');
    }
  }

  return (
    <Card>
      <CardHead>
        <QuoteText>{objection.exactText || '—'}</QuoteText>
        <StatusBadge $status={objection.status}>{objection.status || 'pending'}</StatusBadge>
      </CardHead>
      <MetaRow>
        <MetaItem>
          <strong>Category:</strong> {objection.category || <Muted>—</Muted>}
        </MetaItem>
        <MetaItem>
          <strong>Persona:</strong>{' '}
          {objection.personaId || <Muted>none linked</Muted>}
        </MetaItem>
        <MetaItem>
          <strong>Rep:</strong> {objection.repEmail || <Muted>—</Muted>}
        </MetaItem>
        <MetaItem>
          <strong>Date:</strong> {formatDate(objection.createdAt)}
        </MetaItem>
      </MetaRow>

      {rowError && <ErrorMessage>{rowError}</ErrorMessage>}

      {objection.status === 'pending' ? (
        <>
          <NoteRow>
            <NoteInput
              type="text"
              placeholder="Optional note (visible on approve/reject)..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </NoteRow>
          <ActionsRow>
            <Button onClick={handleApprove} disabled={busy}>
              {busy ? 'Working...' : 'Approve'}
            </Button>
            <RejectButton onClick={handleReject} disabled={busy}>
              {busy ? 'Working...' : 'Reject'}
            </RejectButton>
            {!objection.personaId && (
              <HelpNote>No persona linked — approving will not add this to a persona's library.</HelpNote>
            )}
          </ActionsRow>
          {rowNotice && <HelpNote style={{ display: 'block', marginTop: 8 }}>{rowNotice}</HelpNote>}
        </>
      ) : (
        objection.note && <HelpNote>Note: {objection.note}</HelpNote>
      )}
    </Card>
  );
}

function ObjectionReview({ user }) { // eslint-disable-line no-unused-vars
  const [objections, setObjections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const unsub = subscribeObjections(
      (rows) => {
        setObjections(rows);
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        setLoadError(
          err?.code === 'permission-denied'
            ? "You don't have permission to view the objection queue. Contact an admin if this seems wrong."
            : 'Could not load objections. Please refresh and try again.'
        );
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: objections.length };
    for (const o of objections) {
      const s = o.status || 'pending';
      if (c[s] != null) c[s] += 1;
    }
    return c;
  }, [objections]);

  const visible = useMemo(() => {
    if (filter === 'all') return objections;
    return objections.filter((o) => (o.status || 'pending') === filter);
  }, [objections, filter]);

  async function handleApprove(objection, note) {
    setBusyId(objection.id);
    try {
      if (objection.personaId) {
        await addKnownObjection(objection.personaId, objection.exactText);
      }
      await setObjectionStatus(objection.id, 'approved', note);
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(objection, note) {
    setBusyId(objection.id);
    try {
      await setObjectionStatus(objection.id, 'rejected', note);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <SectionTitle>Objection review</SectionTitle>
      <SectionSubtitle>
        Approve objections reps flagged during calls to add them to a persona's known
        objections library, or reject ones that don't belong.
      </SectionSubtitle>

      {loading && <EmptyState>Loading objections...</EmptyState>}
      {!loading && loadError && <ErrorState>{loadError}</ErrorState>}

      {!loading && !loadError && (
        <>
          <FilterBar>
            <FilterLabel>Status</FilterLabel>
            {FILTERS.map((f) => (
              <Chip key={f.key} $active={filter === f.key} onClick={() => setFilter(f.key)}>
                {f.label}
                <ChipCount $active={filter === f.key}>{counts[f.key] || 0}</ChipCount>
              </Chip>
            ))}
          </FilterBar>

          {visible.length === 0 ? (
            <EmptyState>Nothing here.</EmptyState>
          ) : (
            <CardList>
              {visible.map((o) => (
                <ObjectionCard
                  key={o.id}
                  objection={o}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  busy={busyId === o.id}
                />
              ))}
            </CardList>
          )}
        </>
      )}
    </div>
  );
}

export default ObjectionReview;
